import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { Alert, Box, Paper, Skeleton, Tab, Tabs, useTheme } from '@mui/material';
import { Typography } from '@mui/material';
import type { GridPaginationModel } from '@mui/x-data-grid-pro';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { useSupervisorQueueContent } from './useSupervisorQueueContent';
import { DATAGRID_PAGE_SIZE_OPTIONS } from '../../../common/constants';
import { DataGridToolbar } from '../../../components/DatagridToolbar/DataGridToolbar';
import { useAuth } from '../../../core/auth';
import { useAppDispatch, useAppSelector } from '../../../lib/store';
import { DataGridLayout } from '../../../wrapper-components/DataGrid/components/DataGridLayout/DataGridLayout';
import { DataGrid } from '../../../wrapper-components/DataGrid/DataGrid';
import { Dropdown } from '../../../wrapper-components/Dropdown/Dropdown';
import { useSupervisorTaskDataGridFetch } from '../hook/useDatagridSupervisorTaskFetch';
import {
  selectBatchId,
  selectProjectId,
  selectProjectsListPage,
  selectProjectsListPageSize,
} from '../state/selector';
import { setProjectsListPaginationModel } from '../state/supervisorQueueSlice';

export const SupervisorQueueContent = () => {
  const {
    projectOptions,
    isProjectsLoading,
    batchOptions,
    isBatchesLoading,
    handleProjectChange,
    handleBatchChange,
    columns,
    isAdminSupervisorForSelectedProject,
  } = useSupervisorQueueContent();
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const { user } = useAuth();
  const projectId = useAppSelector(selectProjectId);
  const batchId = useAppSelector(selectBatchId);

  const dispatch = useAppDispatch();
  const projectsListPage = useAppSelector(selectProjectsListPage);
  const projectsListPageSize = useAppSelector(selectProjectsListPageSize);

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    dispatch(setProjectsListPaginationModel({ page: model.page, pageSize: model.pageSize }));
  };

  const prevProjectRef = useRef(projectId);
  const prevBatchRef = useRef(batchId);

  const { refetch } = useSupervisorTaskDataGridFetch() || {};
  useEffect(() => {
    if (prevProjectRef.current !== projectId || prevBatchRef.current !== batchId) {
      dispatch(setProjectsListPaginationModel({ page: 0, pageSize: projectsListPageSize }));
    }
    prevProjectRef.current = projectId;
    prevBatchRef.current = batchId;
  }, [projectId, batchId]);

  const location = useLocation();

  useEffect(() => {
    if (projectId && batchId) {
      refetch();
    }
  }, [location.key, projectId, batchId]);

  const isReqSelectionDone = projectId && batchId;

  const emptyStateMessageIds =
    projectOptions.length === 0
      ? user.isAdmin
        ? {
            title: 'workspaceCommon.content.table.noProjectsAvailable.title',
            description: 'workspaceCommon.content.table.noProjectsAvailable.description',
          }
        : {
            title: 'workspaceCommon.content.table.noProjectAssigned.title',
            description: 'workspaceCommon.content.table.noProjectAssigned.description',
          }
      : !projectId
      ? {
          title: 'workspaceCommon.content.table.noProjectSelected.title',
          description: 'workspaceCommon.content.table.noProjectSelected.description',
        }
      : !isBatchesLoading && batchOptions.length === 0
      ? {
          title: 'workspaceCommon.content.table.noBatchesAvailable.title',
          description: 'workspaceCommon.content.table.noBatchesAvailable.description',
        }
      : {
          title: 'workspaceCommon.content.table.noBatchSelected.title',
          description: 'workspaceCommon.content.table.noProjectSelected.description',
        };

  return (
    <Box>
      <Box>
        <Box sx={{ display: 'flex', gap: theme.spacing(4), pb: theme.spacing(4) }}>
          <Dropdown
            id="supervisor-queue-content-project-dropdown"
            options={projectOptions}
            onChange={handleProjectChange}
            label={formatMessage({ id: 'workspaceCommon.content.dropdown.project.label' })}
            placeholder={formatMessage({
              id: 'workspaceCommon.content.dropdown.project.placeholder',
            })}
            disabled={isProjectsLoading || projectOptions.length === 0}
            width="medium"
            value={projectId ?? ''}
          />
          <Dropdown
            id="supervisor-queue-content-batch-dropdown"
            options={batchOptions}
            onChange={handleBatchChange}
            label={formatMessage({ id: 'workspaceCommon.content.dropdown.batch.label' })}
            placeholder={formatMessage({
              id: 'workspaceCommon.content.dropdown.batch.placeholder',
            })}
            disabled={isBatchesLoading || batchOptions.length === 0}
            width="medium"
            value={batchId ?? ''}
          />
        </Box>
        {isAdminSupervisorForSelectedProject && (
          <Alert
            icon={<SupervisorAccountIcon fontSize="inherit" />}
            severity="info"
            sx={{ mb: theme.spacing(3) }}
          >
            {formatMessage({ id: 'supervisorQueue.demarkation.adminAsSupervisorForProject' })}
          </Alert>
        )}

        {isProjectsLoading ? (
          <Box>
            <Skeleton
              variant="rectangular"
              width="100%"
              height="348px"
              sx={{ transform: 'none' }}
              data-testid="supervisor-queue-content-project-options-skeleton"
            />
          </Box>
        ) : !isReqSelectionDone ? (
          <Box>
            <Paper
              sx={{
                height: '348px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: theme.spacing(2),
              }}
            >
              <Typography variant="h5">
                {formatMessage({ id: emptyStateMessageIds.title })}
              </Typography>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                {formatMessage({ id: emptyStateMessageIds.description })}
              </Typography>
            </Paper>
          </Box>
        ) : (
          <DataGridLayout id="supervisor-queue-content-data-grid">
            <DataGrid
              id="supervisor-queue-content-data-grid"
              mode="client"
              pagination
              loadOnFetch
              pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
              paginationModel={{ page: projectsListPage, pageSize: projectsListPageSize }}
              onPaginationModelChange={handlePaginationModelChange}
              disableSearchParamSync
              preventUrlReadWhenSyncDisabled
              getRowId={(row) => row.id}
              columns={columns}
              useFetchDataGridData={useSupervisorTaskDataGridFetch}
              slots={{ toolbar: DataGridToolbar }}
              sx={{
                maxHeight: '70vh',
                '& .MuiDataGrid-row': {
                  borderBottom: `1px solid ${theme.palette.divider}`,
                },
                '& .MuiDataGrid-cell--pinnedLeft': {
                  borderRight: `2px solid ${theme.palette.divider}`,
                },
                '& .MuiDataGrid-columnHeader--pinnedLeft': {
                  borderRight: `2px solid ${theme.palette.divider}`,
                },
              }}
              initialState={{
                pinnedColumns: {
                  left: ['encounterId'],
                },
                sorting: {
                  sortModel: [
                    {
                      field: 'status',
                      sort: 'asc',
                    },
                  ],
                },
              }}
            />
          </DataGridLayout>
        )}
      </Box>
    </Box>
  );
};
